import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '../utils/propStyles';
import Badge from './Badge';
import Icon from './Icon';
import Translate, { translate } from './Translate';
import FilterSearch from './FilterSearch';
import FilterValueComponent from './FilterValue';
import { FilterItemProps, FilterValue } from '../models/components/Filter';
import { camelCase } from 'change-case';
import { KeyboardEventHelper } from '../utils/KeyboardEventHelper';

const FilterPropertyHeader = styled<any, any>('label')`
    ${propStyles};
    cursor: pointer;
`;

FilterPropertyHeader.defaultProps = {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    fontFamily: 'heading',
    fontWeight: 'medium',
    color: 'body',
};

const FilterListWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FilterListWrapper.defaultProps = {
    flex: '1 0 auto',
    paddingX: 'md',
    paddingY: 'md',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: ['none none solid none', 'none none solid none', 'none'],
};

const FilterList = styled<any, any>('ol')`
    ${propStyles};
    list-style: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

FilterList.defaultProps = {
    maxHeight: '300px',
    overflowX: 'hidden',
    overflowY: 'auto',
};

const ClearIndividualFiltersButton = styled<any, any>('button')`
    ${propStyles};
`;

ClearIndividualFiltersButton.defaultProps = {
    display: 'flex',
    fontFamily: 'body',
    fontWeight: 'normal',
    fontSize: 'sm',
    color: 'gray500',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    outline: 'none',
    selected: {
        color: 'body',
    },
};

const rightAlignStyleProps = {
    order: 1,
};
const leftAlignStyleProps = {
    margin: '0 0 0 auto',
};
const SelectedFilterInfo = styled<any, any>('div')`
    ${propStyles};
`;

SelectedFilterInfo.defaultProps = {
    display: 'flex',
    flex: '1',
};

const Divider = styled<any, any>('hr')`
    ${propStyles};
`;

Divider.defaultProps = {
    borderWidth: '1px',
    borderColor: 'gray500',
    borderStyle: 'dotted none none none',
};

@inject('translationStore')
@inject('filterStore')
@inject('accessibilityStore')
@observer
class FilterItem extends Component<FilterItemProps> {
    onKeyDown = event => {
        if (KeyboardEventHelper.isAcceptKey(event)) {
            this.props.toggleExpanded(event);
        }
    };

    renderFilterValue = (
        filterValue: FilterValue,
        state: { active: boolean; checked: boolean },
        themeOverride?
    ) => {
        const { filterStore, filter, toggleExpanded, theme } = this.props;
        const {
            property,
            filterSearchKey,
            ui,
            allowInactiveSelection = true,
        } = filter;
        const { active, checked } = state;
        const disabled = allowInactiveSelection === false && active === false;
        const onChange = e => {
            filterStore.toggleFilter(
                property,
                filterValue.value,
                filterSearchKey,
                checked,
                filter
            );

            if (ui === 'dropdown') {
                toggleExpanded(e);
            }
        };

        return (
            <FilterValueComponent
                key={`${filterValue.label}`}
                label={filterValue.label}
                ui={ui}
                value={filterValue.value}
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                active={active}
                filterProperty={property}
                {...(themeOverride || theme.FilterListItem)}
            />
        );
    };

    render() {
        const {
            theme,
            translationStore,
            filterStore,
            filter,
            accessibilityStore,
            // withFilter HoC props
            filterId,
            expanded,
            toggleExpanded,
            selectedCount,
            total,
            search,
            clearSearch,
            selectedValues,
            filteredValues,
            filterSearch,
            enableSearch,
        } = this.props;
        const isDropdown = filter.ui === 'dropdown';

        return (
            <FilterListWrapper>
                <FilterPropertyHeader
                    htmlFor={filterId}
                    onClick={e => toggleExpanded(e)}
                    aria-expanded={expanded}
                    aria-controls={`${filterId}-expanded-content`}
                    data-metric-id={`filterHeader-side-${camelCase(
                        filter.label
                    )}`}
                    tabIndex={0}
                    onKeyDown={this.onKeyDown}
                    role="button"
                    aria-describedby="FiltersHeaderToggle"
                    {...theme.FilterPropertyHeader}
                >
                    <Icon
                        fileName={
                            expanded
                                ? 'FilterItemHeaderIconExpanded'
                                : 'FilterItemHeaderIcon'
                        }
                        aria-hidden="true"
                        {...theme.FilterItemHeaderIcon}
                    />
                    <Translate>{filter.label}</Translate>
                    {selectedCount > 0 && (
                        <SelectedFilterInfo>
                            {filterStore.clearIndividualFilters.enabled && (
                                <ClearIndividualFiltersButton
                                    aria-label={accessibilityStore.buttonAccessibilityValue(
                                        'ClearIndividualFiltersButton'
                                    )}
                                    {...(filterStore.clearIndividualFilters
                                        .align === 'right'
                                        ? rightAlignStyleProps
                                        : leftAlignStyleProps)}
                                    {...theme.ClearIndividualFiltersButton}
                                    onClick={
                                        filterStore.filtersActive
                                            ? event => {
                                                  event.stopPropagation();
                                                  filterStore.clearFiltersOfType(
                                                      filter.property,
                                                      filter.filterSearchKey
                                                  );
                                              }
                                            : () => undefined
                                    }
                                    data-metric-id={`filterHeader-side-${camelCase(
                                        filter.label
                                    )}-clear`}
                                >
                                    <Icon
                                        height="1.4em"
                                        width="1.4em"
                                        margin="0 0 0 0.25em"
                                        fileName="ClearFiltersIcon"
                                        {...theme.ClearFiltersIcon}
                                    />
                                </ClearIndividualFiltersButton>
                            )}
                            {!isDropdown && (
                                <Badge
                                    {...(filterStore.clearIndividualFilters
                                        .align === 'right' &&
                                        leftAlignStyleProps)}
                                    data-selected={selectedCount > 0}
                                    selected={{
                                        bgColor: 'primary',
                                    }}
                                    {...theme.FilterTotalBadge}
                                >
                                    {selectedCount}/{total}
                                </Badge>
                            )}
                        </SelectedFilterInfo>
                    )}
                </FilterPropertyHeader>
                {expanded && (
                    <div id={`${filterId}-expanded-content`}>
                        {enableSearch && (
                            <FilterSearch
                                id={filterId}
                                type="text"
                                placeholder={`${translate(
                                    translationStore,
                                    'Search'
                                )} ${translate(
                                    translationStore,
                                    filter.label
                                )}`}
                                value={search}
                                onChange={e => filterSearch(e.target.value)}
                                onClear={clearSearch}
                                showClear={search.length > 0}
                            />
                        )}

                        <FilterList {...theme.SelectedFilterList}>
                            {selectedValues.map(value =>
                                this.renderFilterValue(
                                    value,
                                    { checked: true, active: true },
                                    theme.SelectedFilterListItem
                                )
                            )}
                        </FilterList>
                        {selectedValues.length > 0 &&
                            filteredValues.length > 0 && (
                                <Divider {...theme.FilterListDivider} />
                            )}
                        <FilterList {...theme.FilterList}>
                            {filteredValues.reduce(
                                (acc, filteredValue, valueIndex) => {
                                    const active = filteredValue.isActive;
                                    if (
                                        !isDropdown ||
                                        !filter.dropdownOptions ||
                                        !filter.dropdownOptions.hideInactive ||
                                        active
                                    ) {
                                        acc.push(
                                            this.renderFilterValue(
                                                filteredValue,
                                                { active, checked: false },
                                                theme.FilterListItem
                                            )
                                        );
                                    }
                                    return acc;
                                },
                                []
                            )}
                        </FilterList>
                    </div>
                )}
            </FilterListWrapper>
        );
    }
}

export default withTheme(FilterItem);



// WEBPACK FOOTER //
// ./src/Components/FilterItem.tsx